import { motion } from "framer-motion";
import { Code2, Brain, Globe2, Boxes, HardDrive, Cpu, ArrowDown } from "lucide-react";

const LAYERS = [
    { n: "Layer 6", title: "Application Layer", icon: Code2,
      sub: "Developer Console · SDKs · APIs · Deployment Tools",
      items: ["dApps", "AI Apps", "Websites", "Microservices", "Workflows"],
      tint: "from-cyan-100/40 to-cyan-50/0", darkTint: "dark:from-cyan-500/15 dark:to-cyan-500/0" },
    { n: "Layer 5", title: "AI Layer", icon: Brain,
      sub: "GPU Scheduling · Model Hosting · Inference · Fine-tuning",
      items: ["GPU Marketplace", "Model Registry", "Inference Routing", "Training Jobs"],
      tint: "from-violet-100/40 to-violet-50/0", darkTint: "dark:from-violet-500/15 dark:to-violet-500/0" },
    { n: "Layer 4", title: "DCX Name Service", icon: Globe2,
      sub: "On-Chain DNS · Decentralized Routing",
      items: ["Domains", "Routes", "Service Records", "TLS Anchors"],
      tint: "from-sky-100/40 to-sky-50/0", darkTint: "dark:from-sky-500/15 dark:to-sky-500/0" },
    { n: "Layer 3", title: "DeCloudX Layer-1", icon: Boxes,
      sub: "EVM Chain · Settlement · Governance · Validators · Smart Contracts",
      items: ["$DCX Gas", "Smart Contracts", "DPoS-BFT", "Rev-Share", "Governance"],
      tint: "from-violet-100/60 via-blue-100/40 to-cyan-100/40",
      darkTint: "dark:from-violet-500/25 dark:via-blue-500/15 dark:to-cyan-500/15",
      primary: true },
    { n: "Layer 2", title: "Storage Layer", icon: HardDrive,
      sub: "AES-256-GCM · Reed-Solomon 16/10 · Proof of Storage",
      items: ["Encrypted Shards", "Erasure Coding", "Storage Proofs", "Replication"],
      tint: "from-blue-100/40 to-blue-50/0", darkTint: "dark:from-blue-500/15 dark:to-blue-500/0" },
    { n: "Layer 1", title: "Compute Layer", icon: Cpu,
      sub: "Containerized Workloads · Node Runtime · Auto-scaling · Workload Routing",
      items: ["Containers", "WASM", "Bandwidth", "Auto-Scale", "Failover"],
      tint: "from-emerald-100/40 to-emerald-50/0", darkTint: "dark:from-emerald-500/15 dark:to-emerald-500/0" },
];

export default function Solution() {
    return (
        <section id="solution" className="relative py-16 sm:py-24 md:py-32 lg:py-40 border-t border-line overflow-hidden" data-testid="solution-section">
            <div className="absolute inset-0 hero-glow-bg opacity-50 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
                <div className="max-w-3xl mb-10 sm:mb-16">
                    <div className="overline mb-4">02 — The Solution</div>
                    <h2 className="font-heading font-black text-[1.85rem] sm:text-4xl lg:text-5xl leading-[1.06] tracking-tight text-ink">
                        One Protocol.<br />
                        Six Layers.<br />
                        <span className="brand-gradient-text">All Coordinated On-Chain.</span>
                    </h2>
                    <p className="mt-5 sm:mt-6 text-[15px] sm:text-base text-muted-ink max-w-2xl leading-relaxed">
                        DeCloudX is a hybrid-decentralized cloud protocol. Compute, storage, AI workloads, DNS, and settlement are all vertically integrated — coordinated by a Layer-1 EVM chain that verifies nodes, payments, proofs, and developer revenue on-chain.
                    </p>
                </div>

                <div className="relative" data-testid="architecture-stack">
                    {LAYERS.map((layer, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 16, scale: 0.98 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: i * 0.08, ease: "easeOut" }}
                            className="relative mb-4 last:mb-0"
                            style={{ zIndex: LAYERS.length - i }}
                        >
                            <div className={`relative surface rounded-2xl overflow-hidden ${layer.primary ? "ring-1 ring-brand-blue/30 shadow-md" : ""}`}>
                                <div className={`absolute inset-0 bg-gradient-to-r ${layer.tint} ${layer.darkTint} opacity-80 pointer-events-none`} />
                                <div className="relative grid md:grid-cols-12 gap-4 sm:gap-5 p-4 sm:p-5 md:p-7 items-start md:items-center">
                                    <div className="md:col-span-1 flex md:block items-center gap-3">
                                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${layer.primary ? "brand-gradient-bg" : "bg-card border border-line"} flex items-center justify-center shadow-sm shrink-0`}>
                                            <layer.icon size={18} className={layer.primary ? "text-white" : "text-ink"} strokeWidth={1.5} />
                                        </div>
                                        <div className="md:hidden">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">{layer.n}</div>
                                            <div className={`font-heading font-bold text-base mt-0.5 ${layer.primary ? "brand-gradient-text" : "text-ink"}`}>
                                                {layer.title}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="hidden md:block md:col-span-4">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">{layer.n}</div>
                                        <div className={`font-heading font-bold text-lg md:text-xl mt-1 ${layer.primary ? "brand-gradient-text" : "text-ink"}`}>
                                            {layer.title}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint-ink mt-2">{layer.sub}</div>
                                    </div>
                                    <div className="md:hidden">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint-ink">{layer.sub}</div>
                                    </div>
                                    <div className="md:col-span-7 flex flex-wrap gap-1.5 sm:gap-2">
                                        {layer.items.map((it, k) => (
                                            <span key={k} className="chip">{it}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {i < LAYERS.length - 1 && (
                                <div className="flex justify-center my-1.5">
                                    <ArrowDown size={14} className="text-faint-ink/60" strokeWidth={1.5} />
                                </div>
                            )}
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
